import { Box, Button, Flex, Icon, Input, InputGroup, Text } from "@chakra-ui/react";
import { HiOutlineMagnifyingGlass, HiOutlineXMark } from "react-icons/hi2";
import { useEffect, useRef, useState } from "react";
import FilterSelect from "@/pages/clients/components/FilterSelect";
import { hasActiveFilters } from "../scenariosQuery";

/**
 * Search box (debounced) + "responsible person" / "client" / created-date-range filters.
 * `responsibleUsers` and `clients` are `{ value, label }[]`.
 */
const ScenariosToolbar = ({ query, onChange, onClear, responsibleUsers, clients }) => {
    const [searchDraft, setSearchDraft] = useState(query.search);
    const committedSearch = useRef(query.search);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        committedSearch.current = query.search;
        setSearchDraft(query.search);
    }, [query.search]);

    useEffect(() => {
        if (searchDraft === committedSearch.current) return undefined;
        const id = setTimeout(() => {
            committedSearch.current = searchDraft.trim();
            onChangeRef.current({ search: searchDraft.trim() });
        }, 300);
        return () => clearTimeout(id);
    }, [searchDraft]);

    const dateInputStyle = {
        variant: "primary",
        size: "sm",
        type: "date",
        width: "160px",
        color: "white",
        css: { "::-webkit-calendar-picker-indicator": { filter: "invert(1)" } },
    };

    return (
        <Flex w="full" gap={3} direction={{ base: "column", lg: "row" }} align={{ base: "stretch", lg: "center" }} flexWrap="wrap">
            <Box flex="1" minW={{ base: "full", md: "260px" }}>
                <InputGroup startElement={<Icon color="text.grey"><HiOutlineMagnifyingGlass /></Icon>}>
                    <Input
                        variant="primary"
                        fontSize={16}
                        py={5}
                        placeholder="Search scenarios by name…"
                        value={searchDraft}
                        onChange={(e) => setSearchDraft(e.target.value)}
                    />
                </InputGroup>
            </Box>
            <Flex gap={2} flexWrap="wrap" align="center">
                <FilterSelect
                    label="Responsible"
                    value={query.responsibleUserId || "__all__"}
                    options={[{ value: "__all__", label: "Anyone" }, ...responsibleUsers]}
                    onChange={(v) => onChange({ responsibleUserId: v === "__all__" ? "" : v })}
                    width="210px"
                />
                <FilterSelect
                    label="Client"
                    value={query.ownerId || "__all__"}
                    options={[{ value: "__all__", label: "Any client" }, ...clients]}
                    onChange={(v) => onChange({ ownerId: v === "__all__" ? "" : v })}
                    width="210px"
                />
                <Flex gap={1} align="center">
                    <Text color="text.grey" fontSize="sm">Created:</Text>
                    <Input
                        {...dateInputStyle}
                        value={query.createdFrom}
                        max={query.createdTo || undefined}
                        onChange={(e) => onChange({ createdFrom: e.target.value })}
                        aria-label="Created from"
                    />
                    <Text color="text.grey" fontSize="sm">–</Text>
                    <Input
                        {...dateInputStyle}
                        value={query.createdTo}
                        min={query.createdFrom || undefined}
                        onChange={(e) => onChange({ createdTo: e.target.value })}
                        aria-label="Created to"
                    />
                </Flex>
                {hasActiveFilters(query) && (
                    <Button size="sm" variant="outlined" color="text.grey" onClick={onClear}>
                        <Icon fontSize={16}><HiOutlineXMark /></Icon>
                        <Text>Clear</Text>
                    </Button>
                )}
            </Flex>
        </Flex>
    );
};

export default ScenariosToolbar;
